import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { scrollStripToSlideIndex } from "@/lib/homeStripScrollPager";

const THUMB_STEP = 92;
const THUMBS_PER_PAGE = 4;

function leftmostThumbIndex(scrollerEl, slideCount) {
  if (slideCount < 2) return 0;

  const track = scrollerEl.firstElementChild;
  if (!track?.children?.length) return 0;

  const sl = scrollerEl.scrollLeft;
  const n = Math.min(track.children.length, slideCount);
  const offsets = [];

  for (let i = 0; i < n; i += 1) {
    const child = track.children[i];
    if (child instanceof HTMLElement) {
      offsets.push(child.offsetLeft);
    }
  }

  if (offsets.length < 2) return 0;

  for (let i = 0; i < offsets.length - 1; i += 1) {
    if (offsets[i] <= sl + 1 && offsets[i + 1] > sl + 1) {
      return i;
    }
  }

  if (sl >= offsets[offsets.length - 1] - 1) {
    return slideCount - 1;
  }

  const step =
    offsets[1] - offsets[0] >= 8 ? offsets[1] - offsets[0] : THUMB_STEP;
  return Math.min(slideCount - 1, Math.max(0, Math.round(sl / step)));
}

function pageFromScroll(scrollerEl, slideCount, pageCount) {
  if (pageCount <= 1) return 0;

  const maxScroll = Math.max(0, scrollerEl.scrollWidth - scrollerEl.clientWidth);
  const sl = scrollerEl.scrollLeft;

  if (maxScroll <= 2) return 0;
  if (sl <= 2) return 0;
  if (sl >= maxScroll - 2) return pageCount - 1;

  const leftmost = leftmostThumbIndex(scrollerEl, slideCount);
  return Math.min(pageCount - 1, Math.floor(leftmost / THUMBS_PER_PAGE));
}

function slideIndexForPage(page, slideCount) {
  return Math.min(slideCount - 1, Math.max(0, page * THUMBS_PER_PAGE));
}

export default function ProductThumbnailStrip({
  images = [],
  selectedImageUrl,
  onImageClick,
  useNativeImg = false,
}) {
  const scrollerRef = useRef(null);
  const [activePage, setActivePage] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  const slideCount = images?.length ?? 0;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(slideCount / THUMBS_PER_PAGE)),
    [slideCount]
  );

  const syncFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || slideCount < 1) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    setHasOverflow(maxScroll > 2);
    setActivePage(pageFromScroll(el, slideCount, pageCount));
  }, [slideCount, pageCount]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || slideCount < 1) return undefined;

    let rafScroll = 0;
    let rafGeo = 0;

    const onScroll = () => {
      cancelAnimationFrame(rafScroll);
      rafScroll = requestAnimationFrame(syncFromScroll);
    };

    const onResize = () => {
      cancelAnimationFrame(rafGeo);
      rafGeo = requestAnimationFrame(syncFromScroll);
    };

    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth + 2) return;
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      if (absX > absY * 0.5) return;
      if (absY < 1) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onResize)
        : null;
    ro?.observe(el);
    const track = el.firstElementChild;
    if (track && ro) ro.observe(track);

    rafGeo = requestAnimationFrame(syncFromScroll);

    return () => {
      cancelAnimationFrame(rafScroll);
      cancelAnimationFrame(rafGeo);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      ro?.disconnect();
    };
  }, [slideCount, images, syncFromScroll]);

  useEffect(() => {
    const idx = images.findIndex((img) => img.imageUrl === selectedImageUrl);
    if (idx < 0) return;
    setActivePage(
      Math.min(pageCount - 1, Math.floor(idx / THUMBS_PER_PAGE))
    );
    if (scrollerRef.current) {
      scrollStripToSlideIndex(scrollerRef.current, idx);
    }
  }, [selectedImageUrl, images, pageCount]);

  const goToPage = useCallback(
    (page) => {
      const el = scrollerRef.current;
      if (!el) return;
      scrollStripToSlideIndex(el, slideIndexForPage(page, slideCount));
    },
    [slideCount]
  );

  const thumbClassName = (isSelected) =>
    `h-[84px] w-[76px] rounded object-cover border ${
      isSelected
        ? "border-primary"
        : "border-transparent hover:border-primary"
    }`;

  if (!slideCount) {
    return (
      <div className="hidden lg:block w-full max-w-[380px]">
        <div className="h-[84px] w-[76px] rounded border border-slate-200" />
      </div>
    );
  }

  const showDots = pageCount > 1 && hasOverflow;

  return (
    <div className="hidden w-full min-w-0 max-w-[380px] lg:block">
      <div
        ref={scrollerRef}
        className="overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x_pan-y] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-max flex-nowrap gap-4">
          {images.map((image, index) => {
            const isSelected = selectedImageUrl === image.imageUrl;
            return (
              <div
                key={image.imageUrl ?? index}
                className="shrink-0 snap-start snap-always"
              >
                <button
                  type="button"
                  aria-label={
                    image.altText
                      ? `Show image: ${image.altText}`
                      : `Show product image ${index + 1}`
                  }
                  aria-current={isSelected ? "true" : undefined}
                  className="block border-0 bg-transparent p-0"
                  onClick={() => onImageClick(image, false, index)}
                >
                  {useNativeImg ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={image.imageUrl}
                      alt=""
                      className={thumbClassName(isSelected)}
                      draggable={false}
                    />
                  ) : (
                    <Image
                      src={image.imageUrl}
                      alt=""
                      width={152}
                      height={168}
                      sizes="76px"
                      quality={72}
                      loading="lazy"
                      fetchPriority="low"
                      draggable={false}
                      className={thumbClassName(isSelected)}
                    />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {showDots ? (
        <div
          className="mt-2 flex max-w-full flex-nowrap justify-center gap-0 overflow-x-auto overflow-y-visible py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [touch-action:pan-x_pan-y] [&::-webkit-scrollbar]:hidden"
          role="tablist"
          aria-label="Product thumbnail pages"
        >
          {Array.from({ length: pageCount }).map((_, i) => {
            const active = i === activePage;
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Thumbnail page ${i + 1} of ${pageCount}`}
                onClick={() => goToPage(i)}
                className="flex h-8 min-h-[32px] w-fit min-w-0 shrink-0 items-center justify-center rounded-full px-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <span
                  className={[
                    "block h-2 rounded-full transition-[width,background-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                    active
                      ? "w-8 bg-primary shadow-sm shadow-primary/30"
                      : "w-2 bg-gray-300 hover:bg-gray-400",
                  ].join(" ")}
                />
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
